import React, { useEffect } from "react";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MuiTelInput } from "mui-tel-input";
import CountryAutocomplete from "../../../components/country-autocomplete";
import { useAccountQuery } from "../../../features/auth/authApi";
import { checkCreds } from "../../../service/api";

interface Shareholder {
  position: string;
  nationality: string;
  fullName: string;
  dateOfBirth: string;
  address: {
    country: string;
    address: string;
    zip: string;
  };
  email: string;
  mobileNumber: string;
  ownershipPercentage: number;
}

interface OwnershipFormValues {
  shareholders: Shareholder[];
}

const initialValues: OwnershipFormValues = {
  shareholders: [
    {
      position: "",
      nationality: "",
      fullName: "",
      dateOfBirth: "",
      address: { country: "", address: "", zip: "" },
      email: "",
      mobileNumber: "",
      ownershipPercentage: 0,
    },
  ],
};

const validationSchema = Yup.object().shape({
  shareholders: Yup.array()
    .of(
      Yup.object().shape({
        position: Yup.string().required("Required"),
        nationality: Yup.string().required("Required"),
        fullName: Yup.string().required("Required"),
        dateOfBirth: Yup.date()
          .required("Required")
          .typeError("Required")
          .max(
            new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
            "Shareholder must be at least 18 years old"
          )
          .min(
            new Date(new Date().setFullYear(new Date().getFullYear() - 110)),
            "Something wrong with age..."
          ),
        address: Yup.object().shape({
          country: Yup.string().required("Country is required"),
          address: Yup.string().required("Address is required"),
          zip: Yup.string().required("Zip code is required"),
        }),
        email: Yup.string().email("Invalid email").required("Required"),
        mobileNumber: Yup.string().required("Required"),
        ownershipPercentage: Yup.number()
          .required("Required")
          .min(0, "Must be at least 0"),
      })
    )
    .test("unique-emails", "Duplicate emails", function (shareholders) {
      if (!shareholders) return true;
      const seen = new Set<string>();
      for (let i = 0; i < shareholders.length; i++) {
        const email = shareholders[i].email ?? "";
        if (seen.has(email)) {
          return this.createError({
            path: `shareholders[${i}].email`,
            message: "Each shareholder must have a unique email",
          });
        }
        seen.add(email);
      }
      return true;
    })
    .test("unique-phones", "Duplicate phones", function (shareholders) {
      if (!shareholders) return true;
      const seen = new Set<string>();
      for (let i = 0; i < shareholders.length; i++) {
        const phone = shareholders[i].mobileNumber ?? "";
        if (seen.has(phone)) {
          return this.createError({
            path: `shareholders[${i}].mobileNumber`,
            message: "Each shareholder must have a unique phone number",
          });
        }
        seen.add(phone);
      }
      return true;
    })
    .test("sum-percentage", "Sum incorrect", function (shareholders) {
      if (!shareholders) return true;
      const total = shareholders.reduce(
        (sum, s) => sum + (s.ownershipPercentage ?? 0),
        0
      );
      if (total < 75 || total > 100) {
        return this.createError({
          path: "shareholders",
          message: "Total ownership must be between 75% and 100%",
        });
      }
      return true;
    }),
});

const CompanyInformationForm3 = () => {
  const [duplicateWarning, setDuplicateWarning] = React.useState<string | null>(
    null
  );
  const [duplicateError, setDuplicateError] = React.useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const { data: account } = useAccountQuery();

  const onSubmit = async (values: OwnershipFormValues) => {
    setDuplicateError(null);
    setDuplicateWarning(null);

    let blockSubmit = false;
    let warnings: string[] = [];

    const currentLogin = account?.user?.login;
    const hasForm3 = !!account?.user?.form3_json;

    for (const shareholder of values.shareholders) {
      try {
        await checkCreds({
          email: shareholder.email,
          phone: shareholder.mobileNumber,
        });
      } catch (err: any) {
        console.error("Check creds error", err);

        const message =
          typeof err === "string"
            ? err
            : err?.message || "Unknown error checking credentials";

        if (!hasForm3 && shareholder.email !== currentLogin) {
          blockSubmit = true;
          setDuplicateError(`${message}`);
          break;
        } else {
          warnings.push(`Duplicate Entry - without changes`);
        }
      }
    }

    if (blockSubmit) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (warnings.length > 0) {
      setDuplicateWarning(warnings.join("\n"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));

    const latestData = JSON.parse(localStorage.getItem("form3_json") || "{}");
    const mergedData = {
      ...latestData,
      shareholders: values.shareholders,
      ...(localStorage.getItem("ownershipStructure") ? { isUBO: true } : {}),
    };
    localStorage.setItem("form3_json", JSON.stringify(mergedData));
    navigate("/separate-corp-info-4");
  };

  const formik = useFormik<OwnershipFormValues>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const errorText = (
    shareholderIndex: number,
    field: keyof Shareholder | `address.${keyof Shareholder["address"]}`
  ): string => {
    const touchedShareholder = formik.touched.shareholders?.[shareholderIndex];
    const errorShareholder = formik.errors.shareholders?.[shareholderIndex];

    if (
      !touchedShareholder ||
      !errorShareholder ||
      typeof errorShareholder === "string"
    ) {
      return "";
    }

    if (field.startsWith("address.")) {
      const subfield = field.split(".")[1] as keyof Shareholder["address"];
      const touched = (touchedShareholder.address as any)?.[subfield];
      const error = (errorShareholder.address as any)?.[subfield];
      return touched && error ? error : "";
    } else {
      const touched = (touchedShareholder as any)?.[field];
      const error = (errorShareholder as any)?.[field];
      return touched && error ? error : "";
    }
  };

  useEffect(() => {
    let shareholders: Shareholder[] = [];
    const acctJson = account?.user.form3_json;
    if (acctJson) {
      try {
        const parsed =
          typeof acctJson === "object" ? acctJson : JSON.parse(acctJson);
        if (Array.isArray(parsed.shareholders)) {
          shareholders = parsed.shareholders;
        }
      } catch {
        console.warn("Cant parse form3_json");
      }
    }

    if (shareholders.length === 0) {
      const stored = localStorage.getItem("ownershipStructure");
      if (stored) {
        try {
          const arr = JSON.parse(stored);
          if (Array.isArray(arr) && arr.length > 0) {
            const o = arr[0];
            shareholders = [
              {
                position: "",
                nationality: o.nationality || "",
                fullName: o.fullName || "",
                dateOfBirth: o.date_of_birth || "",
                address: {
                  country: o.country || "",
                  address:
                    (o.address || "") +
                    " " +
                    (o.city || "") +
                    " " +
                    (o.state || ""),
                  zip: o.zip || "",
                },
                email: o.email || "",
                mobileNumber: o.phone || "",
                ownershipPercentage: 0,
              },
            ];
          }
        } catch {
          console.warn("Cant parse from ls");
        }
      }
    }

    if (shareholders.length > 0) {
      formik.setValues({ shareholders });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormikProvider value={formik}>
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <Container maxWidth="md">
          <form onSubmit={formik.handleSubmit}>
            <Card
              elevation={24}
              component={motion.div}
              layoutId="ownership-card"
            >
              {duplicateError && (
                <Alert
                  severity="error"
                  onClose={() => setDuplicateError(null)}
                  sx={{ mt: 2, whiteSpace: "pre-line" }}
                >
                  {duplicateError}
                </Alert>
              )}

              {duplicateWarning && (
                <Alert
                  severity="warning"
                  onClose={() => setDuplicateWarning(null)}
                  sx={{ mt: 2, whiteSpace: "pre-line" }}
                >
                  {duplicateWarning}
                </Alert>
              )}
              <CardContent>
                <Stack spacing={4}>
                  <Typography variant="h5" fontWeight="600">
                    Ownership Structure
                  </Typography>
                  <Typography>
                    Please confirm the names and addresses of the Ultimate
                    Beneficial Owners (UBOs) holding 25% and above and state
                    their percentage of ownership of the company.
                  </Typography>

                  <FieldArray name="shareholders">
                    {({ push, remove, form }) => (
                      <>
                        {form.values.shareholders.map(
                          (shareholder: Shareholder, index: number) => (
                            <Box key={index} sx={{ mt: 3 }}>
                              <Typography variant="h6">
                                UBO {index + 1}
                              </Typography>
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    label="Position"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].position`
                                    )}
                                    error={!!errorText(index, "position")}
                                    helperText={errorText(index, "position")}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={8}>
                                  <TextField
                                    label="Full Name"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].fullName`
                                    )}
                                    error={!!errorText(index, "fullName")}
                                    helperText={errorText(index, "fullName")}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  <CountryAutocomplete
                                    label="Nationality"
                                    name={`shareholders[${index}].nationality`}
                                    value={
                                      formik.values.shareholders[index]
                                        .nationality
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={errorText(index, "nationality")}
                                    kycProgramId={
                                      account?.user?.wallet?.kyc_program_id
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    label="Date of Birth"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].dateOfBirth`
                                    )}
                                    error={!!errorText(index, "dateOfBirth")}
                                    helperText={errorText(index, "dateOfBirth")}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  {(() => {
                                    const ownProps = formik.getFieldProps(
                                      `shareholders[${index}].ownershipPercentage`
                                    );
                                    return (
                                      <TextField
                                        label="Ownership %"
                                        type="number"
                                        fullWidth
                                        {...ownProps}
                                        InputProps={{
                                          inputProps: {
                                            min: 0,
                                            max: 100,
                                            step: 0.01,
                                          },
                                          sx: {
                                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                              {
                                                WebkitAppearance: "none",
                                                margin: 0,
                                              },
                                          },
                                        }}
                                        onChange={(e) => {
                                          const numericValue = Number(
                                            e.target.value
                                          );
                                          ownProps.onChange({
                                            target: {
                                              name: ownProps.name,
                                              value: numericValue,
                                            },
                                          });
                                        }}
                                        error={
                                          !!errorText(
                                            index,
                                            "ownershipPercentage"
                                          )
                                        }
                                        helperText={errorText(
                                          index,
                                          "ownershipPercentage"
                                        )}
                                      />
                                    );
                                  })()}
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                  <CountryAutocomplete
                                    label="Country"
                                    name={`shareholders[${index}].address.country`}
                                    value={
                                      formik.values.shareholders[index].address
                                        .country
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={errorText(index, "address.country")}
                                    kycProgramId={
                                      account?.user?.wallet?.kyc_program_id
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    label="Address"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].address.address`
                                    )}
                                    error={
                                      !!errorText(index, "address.address")
                                    }
                                    helperText={errorText(
                                      index,
                                      "address.address"
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    label="ZIP code"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].address.zip`
                                    )}
                                    error={!!errorText(index, "address.zip")}
                                    helperText={errorText(index, "address.zip")}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Email"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `shareholders[${index}].email`
                                    )}
                                    error={!!errorText(index, "email")}
                                    helperText={errorText(index, "email")}
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <MuiTelInput
                                    forceCallingCode
                                    focusOnSelectCountry
                                    defaultCountry="US"
                                    label="Mobile Number"
                                    name={`shareholders[${index}].mobileNumber`}
                                    value={
                                      formik.values.shareholders[index]
                                        .mobileNumber
                                    }
                                    onChange={(value) =>
                                      formik.setFieldValue(
                                        `shareholders[${index}].mobileNumber`,
                                        value
                                      )
                                    }
                                    error={Boolean(
                                      formik.touched.shareholders?.[index]
                                        ?.mobileNumber
                                    )}
                                    helperText={errorText(
                                      index,
                                      "mobileNumber"
                                    )}
                                    fullWidth
                                  />
                                </Grid>
                              </Grid>
                              {form.values.shareholders.length > 1 && (
                                <Button
                                  variant="outlined"
                                  onClick={() => remove(index)}
                                  sx={{ mt: 1 }}
                                >
                                  Remove
                                </Button>
                              )}
                            </Box>
                          )
                        )}
                        <Button
                          variant="contained"
                          onClick={() =>
                            push({
                              position: "",
                              nationality: "",
                              fullName: "",
                              dateOfBirth: "",
                              address: { country: "", address: "", zip: "" },
                              email: "",
                              mobileNumber: "",
                              ownershipPercentage: 0,
                            })
                          }
                          sx={{ mt: 2 }}
                        >
                          Add UBO
                        </Button>
                        {typeof formik.errors.shareholders === "string" && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 2 }}
                          >
                            {formik.errors.shareholders}
                          </Typography>
                        )}
                      </>
                    )}
                  </FieldArray>

                  <Stack spacing={2} mt={3}>
                    <LoadingButton
                      loading={formik.isSubmitting}
                      type="submit"
                      variant="contained"
                    >
                      Save & Continue
                    </LoadingButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </form>
        </Container>
      </Box>
    </FormikProvider>
  );
};

export default CompanyInformationForm3;
